import { useState } from 'react'
import { BackButton } from '../components/BackButton'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api'
import { useAsync } from '../lib/useAsync'
import { useToast } from '../components/Toasts'
import { ErrorState, LoadingBlock } from '../components/States'
import { plural } from '../lib/format'
import { matchingLine, storedLine, useLookAgain } from '../lib/survey-status'
import type { MarketSurvey } from '../types'

/**
 * What the company holds, one row per survey.
 *
 * A survey starts as one uploaded file and can take more, so the files it was
 * built from are listed rather than assumed to be one.
 */
export function SurveyList() {
  const surveys = useAsync(() => api.listSurveys(), [])
  // While a survey is being stored or prepared, its line moves (FR-M62).
  useLookAgain(surveys, api.listSurveys)
  const [removing, setRemoving] = useState<MarketSurvey | null>(null)

  const nav = useNavigate()
  if (surveys.loading) return <LoadingBlock label="Loading surveys" />
  if (surveys.error) return <ErrorState message={surveys.error} onRetry={surveys.reload} />

  return (
    <section className="screen">
      {/* The way out, in the corner, like every other screen reached from another. */}
      <BackButton to="market data" onClick={() => nav('/market-data')} />
      <header className="title-row">
        <div>
          <h1 className="page">Surveys</h1>
          <p className="subline">{plural(surveys.data?.length ?? 0, 'survey')} held</p>
        </div>
        <div className="actions">
          <Link className="btn" to="/market-data">Records</Link>
          <Link className="btn btn--primary" to="/market-data/add">Add a survey</Link>
        </div>
      </header>

      {removing && (
        <RemoveSurvey
          survey={removing}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null)
            surveys.reload()
          }}
        />
      )}

      {!surveys.data?.length ? (
        <div className="state state--empty">
          <h2>No surveys yet</h2>
          <p>Upload a provider's file and it appears here.</p>
          <Link className="btn btn--primary" to="/market-data/add">Add a survey</Link>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Year</th>
                <th>Industry</th>
                <th className="num">Records</th>
                <th>Status</th>
                <th>Built from</th>
                <th>Loaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {surveys.data.map((s) => (
                <tr key={s.id}>
                  <td>{s.provider}</td>
                  <td>{s.year}</td>
                  <td>{s.industry || <span className="subline">not stated</span>}</td>
                  <td className="num">{s.rowCount.toLocaleString()}</td>
                  {/*
                    How far it has got (FR-M58, FR-M59): the rows going in,
                    then whether it can be matched against yet. A survey from
                    before this was kept says nothing rather than guessing.
                  */}
                  <td>
                    {s.removing
                      ? null
                      : storedLine(s) ?? (s.storing ? 'Still being stored' : matchingLine(s))}
                  </td>
                  <td>
                    {(s.files ?? []).map((f) => f.fileName).join(', ')}
                  </td>
                  <td>
                    {new Date(s.uploadedAt).toLocaleDateString()}
                    <span className="subline"> by {s.uploadedBy}</span>
                  </td>
                  <td>
                    {/*
                      A survey being emptied stays listed and says so. Nothing
                      else can be started against it, because the rows are
                      going and adding more would race the worker.
                    */}
                    {s.removing ? (
                      <span className="pill pill--meta">Being removed…</span>
                    ) : (
                      <div className="actions actions--nowrap">
                        <Link
                          className="btn btn--sm"
                          to={`/market-data/add?survey=${encodeURIComponent(s.id)}`}
                       >
                          Add rows
                        </Link>
                        <button
                          type="button"
                          className="btn btn--sm btn--danger"
                          onClick={() => setRemoving(s)}
                       >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * Removing a survey, which is the most dangerous thing on these screens.
 *
 * She types the provider's name first. Everywhere else in the product that
 * would be friction for its own sake; here it can be a hundred thousand
 * licensed records with no way back.
 */
function RemoveSurvey({
  survey,
  onClose,
  onDone,
}: {
  survey: MarketSurvey
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)

  const matches = typed.trim().toLowerCase() === survey.provider.trim().toLowerCase()

  async function remove() {
    setBusy(true)
    try {
      const { records } = await api.removeSurvey(survey.id, typed)
      toast.ok(`Removing ${records.toLocaleString()} records.`)
      onDone()
    } catch (err) {
      toast.fail((err as Error).message)
      setBusy(false)
    }
  }

  return (
    <div className="card card--pad confirm" role="alertdialog" aria-labelledby="rm-title">
      <h2 id="rm-title" className="confirm__title">
        Remove {survey.provider} {survey.year}?
      </h2>
      <p className="confirm__body">
        {/*
          Several surveys can read "Mercer 2026", so the provider and year are
          not enough to say which one this is. On the screen that destroys
          data, naming the file is the difference between removing the right
          survey and the wrong one.
        */}
        Built from <b>{(survey.files ?? []).map((f) => f.fileName).join(', ') || 'no file'}</b>.
        {' '}This removes <b>{survey.rowCount.toLocaleString()} records</b>, including any rows
        added to it later. There is no archive and nothing comes back.
      </p>
      <label className="field">
        <span>Type “{survey.provider}” to confirm</span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
      </label>
      <div className="actions">
        <button
          type="button"
          className="btn btn--danger-solid"
          disabled={!matches || busy}
          onClick={() => void remove()}
        >
          {busy ? 'Removing…' : 'Remove the survey'}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}
